// Global Instructions Rule Applied!
//
// Fundraising panel — the LP account Dashboard's per-fundraise state (owner,
// 2026-09-03). Until now that state lived only on each prospect record, so an
// LP's own page could not answer "which funds are we targeting them for, and
// whose move is it". One row per OPEN process, most-neglected first.
//
// Everything judgemental here is the PROSPECT TRACKER's own reading —
// view__lp_fundraising_position delegates the flags and the last-move clock to
// internal_agents__get_prospect_stats, so this panel and the agent's signals
// cannot disagree. The pipeline stage stays the human-owned state, as in the
// rest of the fundraising module.
import React, { useCallback, useEffect, useState } from 'react'
import { Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchLpFundraisingPosition } from '../services/internalAgentsService.ts'
import { LpFundraisingRow } from '../../../state_manager/data_models/internal_agents_model.ts'
import { prospectFormPath } from '../../relationships/utils/openRecord.ts'

interface LpFundraisingCardProps {
  accountId: string
  /** Hands `load` to the account form so its Refresh button reloads this
   *  panel. Same contract the sibling agent panels expose. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
  /** Render nothing when the LP is in no open process, so an LP we are not
   *  currently raising from keeps today's layout. */
  hideWhenEmpty?: boolean
}

const MOVE_LABEL: Record<string, { label: string; color: string; hint: string }> = {
  us: {
    label: 'Our move',
    color: 'orange',
    hint: 'The firm owes the next step: either an inbound is sitting unanswered or our last move is past the follow-up threshold.'
  },
  them: {
    label: 'With them',
    color: 'blue',
    hint: 'We moved last; the investor owes the next response.'
  },
  unknown: {
    label: 'No contact yet',
    color: 'default',
    hint: 'Nothing has been recorded on this process from either side.'
  }
}

const FLAG_LABEL: Record<string, string> = {
  follow_up_due: 'Follow-up due',
  stalled: 'Stalled'
}

export const LpFundraisingCard: React.FC<LpFundraisingCardProps> = ({
  accountId,
  onRefreshReady = null,
  hideWhenEmpty = false
}) => {
  const [rows, setRows] = useState<LpFundraisingRow[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchLpFundraisingPosition(accountId, token()))
    } catch (error) {
      console.error('[internal-agents] LP fundraising position load failed:', error)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale.
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  if (hideWhenEmpty && !loading && rows.length === 0) {
    return null
  }

  const ourMove = rows.filter((r) => r.whose_move === 'us').length

  return (
    <FormSection
      data-component='LpFundraisingCard'
      title='Fundraising'
      titleColor='blue'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerActions={
        rows.length > 0 ? (
          <span className='flex items-center gap-2'>
            <span className='text-gray-400 text-[11.5px]'>
              {rows.length} open {rows.length === 1 ? 'process' : 'processes'}
            </span>
            {ourMove > 0 ? <Tag color='orange'>{ourMove} our move</Tag> : null}
          </span>
        ) : null
      }
    >
      {rows.length === 0 && !loading ? (
        <div className='text-gray-400 text-xs py-2'>No open fundraising process with this investor.</div>
      ) : (
        <div className='flex flex-col divide-y divide-gray-100 -mx-1'>
          {rows.map((row) => {
            const move = MOVE_LABEL[row.whose_move] || MOVE_LABEL.unknown
            const since =
              row.days_since_our_last_move != null
                ? `${row.days_since_our_last_move}d since our last move`
                : row.days_in_stage != null
                  ? `${row.days_in_stage}d in stage, no move from us`
                  : null
            return (
              <div key={row.prospects__id} className='px-1 py-2'>
                <div className='flex items-start justify-between gap-2'>
                  <div className='min-w-0'>
                    <button
                      type='button'
                      className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-[13px] font-semibold text-left'
                      onClick={() => navigate(prospectFormPath(row.prospects__id))}
                    >
                      {row.funds__name || row.prospects__name || 'Fundraising process'}
                    </button>
                    <span className='text-[11.5px] text-gray-500 ml-2'>
                      {[row.pipeline_stages__name, row.pipeline_statuses__name]
                        .filter(Boolean)
                        .join(' · ')}
                      {row.days_in_stage != null ? ` · ${row.days_in_stage}d in stage` : ''}
                    </span>
                  </div>
                  <span className='flex items-center gap-1 shrink-0'>
                    {row.prospects__commitment_fmt ? (
                      <span className='text-[13px] tabular-nums font-semibold'>
                        {row.prospects__commitment_fmt}
                      </span>
                    ) : null}
                    <Tooltip title={move.hint}>
                      <Tag color={move.color} style={{ marginInlineEnd: 0 }}>
                        {move.label}
                      </Tag>
                    </Tooltip>
                  </span>
                </div>

                <div className='flex items-center gap-2 mt-1 flex-wrap'>
                  {row.flags.map((flag) => (
                    <Tag key={flag} color='gold' style={{ fontSize: 10, lineHeight: '16px', marginInlineEnd: 0 }}>
                      {FLAG_LABEL[flag] || flag}
                    </Tag>
                  ))}
                  {row.unanswered_inbound_days != null ? (
                    <Tag color='red' style={{ fontSize: 10, lineHeight: '16px', marginInlineEnd: 0 }}>
                      Unanswered {row.unanswered_inbound_days}d
                    </Tag>
                  ) : null}
                  {row.open_signals_count > 0 ? (
                    <span className='text-[11px] text-gray-400'>
                      {row.open_signals_count} open signal{row.open_signals_count === 1 ? '' : 's'}
                    </span>
                  ) : null}
                  {since ? <span className='text-[11px] text-gray-400'>{since}</span> : null}
                </div>

                {row.next_step ? (
                  <div className='mt-1'>
                    <span className='text-[11px] uppercase tracking-wide text-gray-400'>Next step</span>
                    <div className='whitespace-pre-line text-[12.5px] text-gray-700'>{row.next_step}</div>
                    {row.next_step_at ? (
                      <span className='text-[11px] text-gray-400'>
                        agent-written {dayjs(row.next_step_at).format('MM/DD/YYYY')}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className='mt-1 text-[11.5px] text-gray-400'>
                    {row.agent_instances__id
                      ? 'No next step written yet — awaiting the prospect tracker’s first deep run.'
                      : 'Not followed by the prospect tracker.'}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </FormSection>
  )
}
